<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="radio.id">
          Edit radio
        </div>
        <div v-else-if="parentRadio">
          <div>Create child radio</div>
          <div class="body-2">
            Parent Radio:
            <router-link :to="{ name: 'radio', params: { id: parentRadio.id }}">
              {{ parentRadio.title }}
            </router-link>
          </div>
        </div>
        <div v-else>
          Create new radio
        </div>
      </v-card-title>
    </template>
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <div class="mb-2 font-weight-bold">
          Radio Title
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="radioDirty.title"
            outlined
            :error-messages="errors"
            placeholder="Enter radio title"
          />
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-textarea
            v-model="radioDirty.description"
            outlined
            :error-messages="errors"
            placeholder="Enter radio description"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          For Client
        </div>
        <v-autocomplete
          v-model="radioDirty.client_id"
          solo
          flat
          outlined
          hint="Select if radio is specific for a certain client"
          persistent-hint
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="All Clients"
        />
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="radioDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteRadio()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import RadioModel from '@/models/RadioModel'
import Radios from '@/services/Radios'
import Clients from '@/services/Clients'

export default {
  name: 'EditRadioDialog',
  components: { ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    radio: {
      type: Object,
      default: () => cloneDeep(RadioModel.emptyModel)
    },
    parentRadio: {
      type: Object,
      default: () => undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      radioDirty: cloneDeep(this.radio),
      submitting: false,
      deleting: false,
      clients: {
        loading: false,
        data: []
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    modalVisible(visible) {
      if (!visible) return

      this.radioDirty = cloneDeep(this.radio)

      if (this.parentRadio) {
        this.radioDirty.id = undefined
        this.radioDirty.title = this.parentRadio.title + ' kopija'
      }
    }
  },
  created() {
    this.fetchClients()
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          title: this.radioDirty.title,
          description: this.radioDirty.description,
          client_id: this.radioDirty.client_id
        }

        if (this.radioDirty.id) {
          const result = await Radios.update(this.radioDirty.id, data)

          this.$emit('radio:updated', result)

          this.$store.dispatch('alert', {
            text: 'Radio successfully updated',
            color: 'success'
          })
        } else {
          let result

          if (this.parentRadio) {
            result = await Radios.clone(this.parentRadio.id, data)

            this.$store.dispatch('alert', {
              text: 'Successfully created a child radio',
              color: 'success'
            })
          } else {
            result = await Radios.store(data)

            this.$store.dispatch('alert', {
              text: 'Radio successfully created',
              color: 'success'
            })
          }

          this.$emit('radio:created', result)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting radio',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteRadio() {
      this.deleting = true

      try {
        const result = await Radios.delete(this.radioDirty.id)

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('radio:deleted', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    }
  }
}
</script>
